import { getTabContent, normalizeTabEntry } from '@/features/Dashboard/lib/transformers';
import type { DashboardTab, MarketAppWithTabs, TileBigNumber } from '@/features/Dashboard/types';

import { cloneApp, cloneTile } from './clone';

import type { DashboardEditState, DraftTab, TabDraft } from '../dashboardEdit.types';

/**
 * Map server data to draft structure.
 * Creates deep copies to prevent reference sharing.
 */
export const mapServerDataToDraft = (
  serverData: DashboardTab,
  dashboardName: string,
  activeTabIndex = 0
): {
  draftTabs: DraftTab[];
  tabDrafts: Record<string, TabDraft>;
} => {
  const draftTabs: DraftTab[] = [];
  const tabDrafts: Record<string, TabDraft> = {};

  Object.entries(serverData).forEach(([tabName, tabValue]) => {
    const normalized = normalizeTabEntry(tabName, tabValue);
    const tabId = normalized.id;

    draftTabs.push({
      id: tabId,
      name: tabName,
      sourceName: tabName // Original server name
    });

    // Parse content into draft structure with deep cloning
    const content = getTabContent(tabValue);
    const marketTabApps: MarketAppWithTabs[] = [];
    let tiles: TileBigNumber[] = [];

    content.forEach(app => {
      if ('type' in app) {
        // Deep clone tiles
        tiles = app.tiles.map(tile => cloneTile(tile));
      } else if ('tabs' in app) {
        // Deep clone app
        marketTabApps.push(cloneApp(app));
      }
    });

    tabDrafts[tabId] = { marketTabApps, tiles };
  });

  return { draftTabs, tabDrafts };
};

/**
 * Map draft state to server payload.
 * Includes current dashboard and other dirty dashboards.
 */
export const mapDraftToServerPayload = (
  editState: DashboardEditState
): Record<string, DashboardTab> => {
  const result: Record<string, DashboardTab> = {};

  // Build current dashboard
  result[editState.dashboardName] = buildDashboardFromDraft(
    editState.draftTabs,
    editState.tabDrafts
  );

  // Build other dirty dashboards
  Object.entries(editState.otherDashboardDrafts).forEach(([dashName, draft]) => {
    if (draft && editState.dirtyDashboards.includes(dashName)) {
      result[dashName] = buildDashboardFromDraft(draft.draftTabs, draft.tabDrafts);
    }
  });

  return result;
};

/**
 * Build DashboardTab from draft structure.
 */
export const buildDashboardFromDraft = (
  draftTabs: DraftTab[],
  tabDrafts: Partial<Record<string, TabDraft>>
): DashboardTab => {
  const result: DashboardTab = {};

  draftTabs.forEach(tab => {
    const draft = tabDrafts[tab.id];
    if (!draft) return;

    const content = [];

    // Add tiles widget if any
    if (draft.tiles.length > 0) {
      content.push({
        type: 'tiles' as const,
        tiles: draft.tiles,
        name: 'Tiles',
        metadata: {},
        layout: null,
        id: 'tiles'
      });
    }

    // Add market apps
    content.push(...draft.marketTabApps);

    result[tab.name] = {
      id: tab.id,
      content
    };
  });

  return result;
};

/**
 * Build preview config from current tab draft.
 */
export const buildPreviewConfig = (tiles: TileBigNumber[], marketTabApps: MarketAppWithTabs[]) => {
  const content = [];

  if (tiles.length > 0) {
    content.push({
      type: 'tiles' as const,
      tiles,
      name: 'Tiles',
      metadata: {},
      layout: null,
      id: 'tiles'
    });
  }

  content.push(...marketTabApps);

  return content;
};
